/**
 * Accomplishment moments — THE FLEET PRIMITIVE. Spec:
 * `docs/superpowers/specs/2026-10-20-accomplishment-moments-design.md`; how to copy it into another
 * app: `docs/moments-primitive.md`.
 *
 * Three pieces, and only three:
 *   - `Moment`        — a proven fact, already localized. Built by a PURE function from records the
 *                        server verified; the UI never decides one.
 *   - `<MomentHost/>` — mounted once at the app root; renders the current moment as a transient,
 *                        dismissable banner in a polite live region. Never a modal, never steals focus.
 *   - `useMoment(m)`  — fires `m` at most once per person per `m.id`, and not at all when the person
 *                        turned moments off. Seen-once is recorded in the user's preferences.
 *
 * This module holds the contract (types, context, settings reader); the components live beside it.
 */
import { createContext, useEffect, useState } from 'react';
import type { UserPreferences } from '../../services/complianceApi';

export type MomentKind = 'period-complete' | 'zero-overdue' | 'streak' | 'first-filing';

export interface Moment {
  /** Stable id of the FACT, e.g. "period-complete:2026-09". Seen-once is keyed on it. */
  id: string;
  kind: MomentKind;
  /** Already localized. */
  title: string;
  /** Already localized; only PROVEN figures — a figure that is not proven is left out, never estimated. */
  detail?: string;
  /** false for quiet moments. Never rendered under prefers-reduced-motion. */
  confetti: boolean;
}

export interface MomentHostApi {
  show: (moment: Moment) => void;
}

/** No host mounted (e.g. a component test) ⇒ moments are a silent no-op. */
export const MomentContext = createContext<MomentHostApi>({ show: () => undefined });

/** How long the banner stays up, unless hovered or focused. The confetti itself is under 2 s. */
export const MOMENT_VISIBLE_MS = 4_000;

/** Mirrors the server's cap (`UserPreferences.MaxSeenMoments`). */
export const MAX_SEEN_MOMENTS = 100;

export interface MomentSettings {
  enabled: boolean;
  seen: string[];
}

/** Reads the opaque `moments` field of the user's preferences. Absent ⇒ on, nothing seen. */
export function momentSettings(prefs: UserPreferences | undefined | null): MomentSettings {
  const raw = prefs?.moments;
  return {
    enabled: raw?.enabled !== false,
    seen: Array.isArray(raw?.seen) ? raw.seen.filter((s): s is string => typeof s === 'string') : [],
  };
}

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

/** True when the person asked the OS for reduced motion — moments then show words only. */
export function usePrefersReducedMotion(): boolean {
  const query = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(REDUCED_MOTION)
    : null;
  const [reduced, setReduced] = useState(() => query?.matches ?? false);
  useEffect(() => {
    if (!query) return;
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    query.addEventListener?.('change', onChange);
    return () => query.removeEventListener?.('change', onChange);
  }, [query]);
  return reduced;
}

// Browser storage is a FALLBACK cache only (spec § 2) — it stops a re-fire in this browser when
// the preferences write fails. The record is the user's preferences document.
const LOCAL_SEEN_KEY = 'statetax.moments.seen';

export function seenLocally(id: string): boolean {
  try {
    const raw = window.localStorage.getItem(LOCAL_SEEN_KEY);
    return Array.isArray(raw ? JSON.parse(raw) : null) && (JSON.parse(raw!) as unknown[]).includes(id);
  } catch {
    return false;
  }
}

export function markSeenLocally(id: string): void {
  try {
    const raw = window.localStorage.getItem(LOCAL_SEEN_KEY);
    const list = raw ? (JSON.parse(raw) as unknown[]).filter((s): s is string => typeof s === 'string') : [];
    if (!list.includes(id)) list.push(id);
    window.localStorage.setItem(LOCAL_SEEN_KEY, JSON.stringify(list.slice(-MAX_SEEN_MOMENTS)));
  } catch {
    // Storage blocked (private window, quota) — the preferences record still holds.
  }
}
